<template>
  <div class="w-full overflow-hidden" :class="wrapperClasses">
    <!-- Loading overlay -->
    <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
      <BaseSpinner size="lg" />
    </div>

    <div class="overflow-x-auto">
      <table :class="tableClasses">
        <!-- Header -->
        <thead :class="headerClasses">
          <tr>
            <!-- Checkbox column -->
            <th v-if="selectable" class="w-12 px-4 py-3">
              <input
                type="checkbox"
                :checked="allSelected"
                :indeterminate="someSelected"
                class="rounded border-gray-300 text-primary focus:ring-primary"
                @change="toggleSelectAll"
              />
            </th>

            <!-- Dynamic columns -->
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                column.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : '',
                column.align === 'center' ? 'text-center' : '',
                column.align === 'right' ? 'text-right' : '',
                column.headerClass || ''
              ]"
              :style="column.width ? { width: column.width } : {}"
              @click="column.sortable ? handleSort(column) : null"
            >
              <div class="flex items-center gap-1" :class="column.align === 'center' ? 'justify-center' : column.align === 'right' ? 'justify-end' : ''">
                <slot :name="`header-${column.key}`" :column="column">
                  {{ column.label }}
                </slot>
                <span v-if="column.sortable && sortBy === column.key" class="ml-1">
                  <svg v-if="sortOrder === 'asc'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </th>

            <!-- Actions column -->
            <th v-if="$slots.actions" class="w-24 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>

        <!-- Body -->
        <tbody :class="bodyClasses">
          <template v-if="data.length > 0">
            <tr
              v-for="(row, rowIndex) in data"
              :key="rowKey ? row[rowKey] : rowIndex"
              :class="[
                rowClasses,
                hoverable ? 'hover:bg-gray-50' : '',
                striped && rowIndex % 2 === 1 ? 'bg-gray-50' : '',
                isSelected(row) ? 'bg-primary-50' : '',
                clickable ? 'cursor-pointer' : ''
              ]"
              @click="handleRowClick(row, rowIndex)"
            >
              <!-- Checkbox column -->
              <td v-if="selectable" class="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  :checked="isSelected(row)"
                  class="rounded border-gray-300 text-primary focus:ring-primary"
                  @click.stop
                  @change="toggleSelect(row)"
                />
              </td>

              <!-- Dynamic cells -->
              <td
                v-for="column in columns"
                :key="column.key"
                :class="[
                  'px-4 py-3 text-sm',
                  column.align === 'center' ? 'text-center' : '',
                  column.align === 'right' ? 'text-right' : '',
                  column.cellClass || ''
                ]"
              >
                <slot :name="`cell-${column.key}`" :row="row" :value="getCellValue(row, column)" :index="rowIndex">
                  {{ getCellValue(row, column) }}
                </slot>
              </td>

              <!-- Actions column -->
              <td v-if="$slots.actions" class="px-4 py-3 text-right">
                <slot name="actions" :row="row" :index="rowIndex"></slot>
              </td>
            </tr>
          </template>

          <!-- Empty state -->
          <tr v-else>
            <td :colspan="totalColumns" class="px-4 py-12 text-center">
              <slot name="empty">
                <div class="flex flex-col items-center text-gray-500">
                  <svg class="w-12 h-12 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p class="text-base font-medium">{{ emptyText }}</p>
                  <p v-if="emptySubtext" class="text-sm text-gray-400 mt-1">{{ emptySubtext }}</p>
                </div>
              </slot>
            </td>
          </tr>
        </tbody>

        <!-- Footer -->
        <tfoot v-if="$slots.footer">
          <tr>
            <td :colspan="totalColumns">
              <slot name="footer"></slot>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import BaseSpinner from './BaseSpinner.vue'

export default {
  name: 'BaseTable',
  components: {
    BaseSpinner
  },
  props: {
    columns: {
      type: Array,
      required: true,
      validator: (value) => value.every(col => col.key && col.label)
    },
    data: {
      type: Array,
      default: () => []
    },
    rowKey: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    striped: {
      type: Boolean,
      default: false
    },
    hoverable: {
      type: Boolean,
      default: true
    },
    bordered: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: String,
      default: null
    },
    sortOrder: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    emptyText: {
      type: String,
      default: 'Aucune donnée disponible'
    },
    emptySubtext: {
      type: String,
      default: null
    }
  },
  emits: ['update:selectedRows', 'sort', 'row-click'],
  computed: {
    totalColumns() {
      let count = this.columns.length
      if (this.selectable) count++
      if (this.$slots.actions) count++
      return count
    },
    wrapperClasses() {
      return [
        'relative',
        this.bordered ? 'border border-gray-200 rounded-lg' : ''
      ]
    },
    tableClasses() {
      return [
        'min-w-full divide-y divide-gray-200',
        this.compact ? 'table-compact' : ''
      ]
    },
    headerClasses() {
      return 'bg-gray-50 text-gray-600'
    },
    bodyClasses() {
      return 'bg-white divide-y divide-gray-200'
    },
    rowClasses() {
      return 'transition-colors duration-150'
    },
    allSelected() {
      return this.data.length > 0 && this.selectedRows.length === this.data.length
    },
    someSelected() {
      return this.selectedRows.length > 0 && this.selectedRows.length < this.data.length
    }
  },
  methods: {
    getCellValue(row, column) {
      if (column.formatter) {
        return column.formatter(row[column.key], row)
      }
      // Support nested keys like 'user.name'
      return column.key.split('.').reduce((obj, key) => obj?.[key], row)
    },
    isSelected(row) {
      if (!this.rowKey) {
        return this.selectedRows.includes(row)
      }
      return this.selectedRows.some(selected => selected[this.rowKey] === row[this.rowKey])
    },
    toggleSelect(row) {
      const selected = [...this.selectedRows]
      const index = this.rowKey
        ? selected.findIndex(r => r[this.rowKey] === row[this.rowKey])
        : selected.indexOf(row)

      if (index > -1) {
        selected.splice(index, 1)
      } else {
        selected.push(row)
      }

      this.$emit('update:selectedRows', selected)
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.$emit('update:selectedRows', [])
      } else {
        this.$emit('update:selectedRows', [...this.data])
      }
    },
    handleSort(column) {
      let order = 'asc'
      if (this.sortBy === column.key && this.sortOrder === 'asc') {
        order = 'desc'
      }
      this.$emit('sort', { column: column.key, order })
    },
    handleRowClick(row, index) {
      if (this.clickable) {
        this.$emit('row-click', { row, index })
      }
    }
  }
}
</script>

<style scoped>
.table-compact th,
.table-compact td {
  @apply py-2;
}
</style>
